// OrderForm.jsx
import React, { useState, useEffect } from "react";
import { PlusCircle, Trash2, X, Info } from "lucide-react";
import Swal from "sweetalert2";
import ProductSelector from "./ProductSelector";
import { validateOrder } from "./ValidateOrder";

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatCpfCnpj = (value) => {
  if (!value) return "-";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  } else if (digits.length === 14) {
    return digits.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }
  return value;
};

const formatCurrency = (value) => {
  if (typeof value !== "number") return "-";
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const OrderForm = ({ onClose, onSave, existingOrder }) => {
  const [orderId, setOrderId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderValidationConfig, setOrderValidationConfig] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultOrder = {
    id: null,
    customerName: "",
    customerDocument: "",
    customerState: "",
    seller: JSON.parse(localStorage.getItem("user"))?.id,
    sellerName: JSON.parse(localStorage.getItem("user"))?.name,
    paymentCondition: "",
    paymentMethod: "",
    cashDiscount: 0,
    scheduleDate: getTodayDate(),
    recurrenceCount: 1,
    recurrenceDays: 0,
    items: [],
    subtotal: 0,
    status: "",
    totalDiscount: 0,
    total: 0,
    isSupervised: false,
  };

  const [order, setOrder] = useState(
    existingOrder ? existingOrder : defaultOrder
  );
  const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);
  const [tempQuantities, setTempQuantities] = useState({});

  useEffect(() => {
    if (existingOrder) {
      setOrder(existingOrder);
      setOrderId(existingOrder.id);
    }
  }, [existingOrder]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/validate_order`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => setOrderValidationConfig(data))
      .catch((err) =>
        console.error("Erro ao buscar validações do pedido:", err)
      );
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetch(`${process.env.REACT_APP_API_BASE_URL}/payments`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => setPaymentTerms(data.payments));
    fetch(`${process.env.REACT_APP_API_BASE_URL}/products`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => setProducts(data))
      .catch((err) => console.error("Erro ao buscar produtos:", err));
    setOrder((prevOrder) => ({
      ...prevOrder,
      scheduleDate: getTodayDate(),
    }));
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/customers`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json();
      setCustomers(data.data || []);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    }
  };

  const filteredCustomers = customers.filter(
    (customer) =>
      customer.RAZAO_SOCIAL?.toLowerCase().includes(
        customerSearchTerm.toLowerCase()
      ) ||
      customer.NOME_FANTASIA?.toLowerCase().includes(
        customerSearchTerm.toLowerCase()
      ) ||
      customer.CNPJ?.includes(customerSearchTerm)
  );

  const selectCustomer = (customer) => {
    // Obtém o estado do cliente a partir do ESTADO_CODIGO_IBGE
    let customerState = "";
    if (customer.ESTADO_CODIGO_IBGE) {
      // Mapeamento dos códigos IBGE para siglas de estado
      const estadosIBGE = {
        11: "RO",
        12: "AC",
        13: "AM",
        14: "RR",
        15: "PA",
        16: "AP",
        17: "TO",
        21: "MA",
        22: "PI",
        23: "CE",
        24: "RN",
        25: "PB",
        26: "PE",
        27: "AL",
        28: "SE",
        29: "BA",
        31: "MG",
        32: "ES",
        33: "RJ",
        35: "SP",
        41: "PR",
        42: "SC",
        43: "RS",
        50: "MS",
        51: "MT",
        52: "GO",
        53: "DF",
      };
      customerState = estadosIBGE[customer.ESTADO_CODIGO_IBGE] || "";
    }

    setOrder((prev) => ({
      ...prev,
      customerName: `${customer.CODIGO} - ${customer.RAZAO_SOCIAL}`,
      customerDocument: customer.CNPJ,
      customerState: customerState,
    }));
    setShowCustomerSearch(false);
    setCustomerSearchTerm("");
  };

  const calculateTotals = (orderData) => {
    const { items, paymentCondition, cashDiscount } = orderData;

    // Calcula o subtotal dos itens
    const subtotal = items.reduce(
      (acc, item) => acc + item.quantity * item.price,
      0
    );

    // Calcula o desconto dos itens
    const itemsDiscount = items.reduce((acc, item) => {
      const itemTotal = item.quantity * ( item.price * ( 1 - (( 1 - (parseFloat(item.discount) / 100) ) * ( 1 - (parseFloat(cashDiscount) / 100) ) ) ) ).toFixed(2);
      return acc + itemTotal;
    }, 0);

    // Calcula o total de descontos
    const totalDiscount = itemsDiscount;

    // Calcula o valor final após TODOS os descontos
    const total = subtotal - totalDiscount;

    return {
      subtotal,
      totalDiscount,
      total,
    };
  };

  const updateOrderAndValidatePartially = (updatedOrder) => {
    const totals = calculateTotals(updatedOrder);
    updatedOrder.subtotal = totals.subtotal;
    updatedOrder.totalDiscount = totals.totalDiscount;
    updatedOrder.total = totals.total;
    setOrder(updatedOrder);
  };

  const handleAddProduct = (product) => {
    const existingItem = order.items.find((item) => item.code === product.code);
    if (existingItem) {
      const newQuantity = existingItem.quantity + 5;
      const updatedItems = order.items.map((item) => {
        if (item.code === product.code) {
          return {
            ...item,
            quantity: newQuantity,
            subtotal: item.price * newQuantity * (1 - item.discount / 100),
          };
        }
        return item;
      });
      const updatedOrder = { ...order, items: updatedItems };
      updateOrderAndValidatePartially(updatedOrder);

      Swal.fire({
        icon: "info",
        title: "Atenção",
        text: "Produto já adicionado, quantidade incrementada em 5 unidades.",
        confirmButtonColor: "#dc2626",
      });
    } else {
      const newItem = {
        id: Date.now(),
        product: product.name,
        code: product.code,
        quantity: 5,
        price: product.price,
        discount: 0,
        image: product.image,
        subtotal: product.price * 5,
      };
      const updatedOrder = {
        ...order,
        items: [...order.items, newItem],
      };
      updateOrderAndValidatePartially(updatedOrder);
    }
    setIsProductSelectorOpen(false);
  };

  const handleItemChange = (id, field, value) => {
    let newVal = parseFloat(value) || 0;
    if (field === "discount") {
      const maxItemDiscount =
        orderValidationConfig?.condicoes_venda[0]?.desconto_item_max ?? 15;
      newVal = Math.min(Math.max(0, newVal), maxItemDiscount);
    }
    if (field === "quantity") {
      setTempQuantities((prev) => ({ ...prev, [id]: value }));
      return;
    }
    const newItems = order.items.map((item) => {
      if (item.id === id) {
        const updatedItem = { ...item };
        if (field === "discount") updatedItem.discount = newVal;
        if (field === "price") updatedItem.price = newVal;
        const itemTotal = updatedItem.price * updatedItem.quantity;
        updatedItem.subtotal =
          itemTotal - itemTotal * (updatedItem.discount / 100);
        return updatedItem;
      }
      return item;
    });
    const updatedOrder = { ...order, items: newItems };
    updateOrderAndValidatePartially(updatedOrder);
  };

  const handleQuantityBlur = (id) => {
    const currentValue = tempQuantities[id];
    if (
      currentValue === "" ||
      currentValue === null ||
      currentValue === undefined
    ) {
      updateItemWithNewValue(id, "quantity", 5);
      setTempQuantities((prev) => {
        const newTemp = { ...prev };
        delete newTemp[id];
        return newTemp;
      });
      return;
    }
    const numericValue = parseInt(currentValue) || 0;
    if (numericValue <= 0) {
      updateItemWithNewValue(id, "quantity", 5);
      Swal.fire({
        icon: "info",
        title: "Ajuste Automático",
        text: "A quantidade não pode ser zero. Foi ajustada para 5 unidades.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } else if (numericValue % 5 !== 0) {
      const adjustedValue = Math.ceil(numericValue / 5) * 5;
      updateItemWithNewValue(id, "quantity", adjustedValue);
      Swal.fire({
        icon: "info",
        title: "Ajuste Automático",
        text: `A quantidade foi ajustada de ${numericValue} para ${adjustedValue} para atender à regra de múltiplos de 5.`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } else {
      updateItemWithNewValue(id, "quantity", numericValue);
    }
    setTempQuantities((prev) => {
      const newTemp = { ...prev };
      delete newTemp[id];
      return newTemp;
    });
  };

  const updateItemWithNewValue = (id, field, adjustedValue) => {
    const newItems = order.items.map((item) => {
      if (item.id === id) {
        const updatedItem = { ...item };
        if (field === "quantity") updatedItem.quantity = adjustedValue;
        const itemTotal = updatedItem.price * updatedItem.quantity;
        updatedItem.subtotal =
          itemTotal - itemTotal * (updatedItem.discount / 100);
        return updatedItem;
      }
      return item;
    });
    const updatedOrder = { ...order, items: newItems };
    updateOrderAndValidatePartially(updatedOrder);
  };

  const handleRemoveItem = (id) => {
    const newItems = order.items.filter((item) => item.id !== id);
    const updatedOrder = { ...order, items: newItems };
    updateOrderAndValidatePartially(updatedOrder);
  };

  const handleSelectPaymentTerm = (code) => {
    let paymentMethod = "credit";

    // Para pagamento à vista (código 003)
    if (code === "003") {
      paymentMethod = "cash";
    }

    // Reseta o desconto adicional para 0 ao trocar a condição de pagamento
    const updatedOrder = {
      ...order,
      paymentCondition: code,
      paymentMethod: paymentMethod,
      cashDiscount: 0,
    };
    updateOrderAndValidatePartially(updatedOrder);
  };

  const handleCashDiscountChange = (value) => {
    // Remove caracteres não numéricos e converte para número
    let numericValue = value.replace(/[^\d.]/g, "");

    // Garante que só há um ponto decimal
    const parts = numericValue.split(".");
    if (parts.length > 2) {
      numericValue = parts[0] + "." + parts.slice(1).join("");
    }

    // Converte para número e limita a duas casas decimais
    let newDiscount = parseFloat(numericValue) || 0;
    newDiscount = Math.round(newDiscount * 100) / 100;

    // Obtém o desconto máximo permitido para a condição de pagamento atual
    let maxDiscount = 0;
    if (order.paymentCondition === "003") {
      maxDiscount = 5;
    } else if (order.paymentCondition) {
      const payment = paymentTerms.find(
        (pt) => pt.E4_CODIGO === order.paymentCondition
      );
      if (payment) {
        const descontosPermitidos = {
          "21 DD": 4,
          "14/28 DD": 4,
          "14/21/28 DD": 4,
          "28 DD": 3,
          "21/35 DD": 3,
          "21/28/35 DD": 3,
          "35 DD": 2,
          "21/49 DD": 2,
          "28/35/42 DD": 2,
          "28/42 DD": 2,
          "21/35/49 DD": 2,
          "42 DD": 1,
          "28/56 DD": 1,
          "28/42/56 DD": 1,
          "21/35/49/63 DD": 1,
        };
        maxDiscount = descontosPermitidos[payment.E4_DESCRI.trim()] || 0;
      }
    }

    // Limita o desconto ao máximo permitido
    newDiscount = Math.min(Math.max(0, newDiscount), maxDiscount);

    const updatedOrder = { ...order, cashDiscount: newDiscount };
    updateOrderAndValidatePartially(updatedOrder);
  };

  const handleFieldChange = (field, value) => {
    const updatedOrder = { ...order, [field]: value };
    updateOrderAndValidatePartially(updatedOrder);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Ajustar quaisquer valores temporários de quantidade pendentes
    const tempQuantityIds = Object.keys(tempQuantities);
    if (tempQuantityIds.length > 0) {
      tempQuantityIds.forEach((id) => handleQuantityBlur(parseInt(id)));
    }

    if (!orderValidationConfig) {
      return Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Aguardando configuração de validação do pedido.",
        confirmButtonColor: "#dc2626",
      });
    }

    if (!order.customerName.trim()) {
      return Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Por favor, selecione um cliente válido.",
        confirmButtonColor: "#dc2626",
      });
    }
    if (!order.scheduleDate) {
      return Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Por favor, selecione a data de programação.",
        confirmButtonColor: "#dc2626",
      });
    }
    if (order.items.length === 0) {
      return Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Adicione pelo menos um item ao pedido.",
        confirmButtonColor: "#dc2626",
      });
    }
    if (order.paymentCondition === "") {
      return Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Adicione a condição de pagamento.",
        confirmButtonColor: "#dc2626",
      });
    }
    const invalidItems = order.items.filter(
      (item) => item.quantity <= 0 || item.price <= 0
    );
    if (invalidItems.length > 0) {
      return Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Existem itens com quantidade ou preço inválidos.",
        confirmButtonColor: "#dc2626",
      });
    }

    // VALIDAÇÃO FINAL E OBRIGATÓRIA DO VALOR MÍNIMO - Não pode ser ignorada
    if (!order.isSupervised) {
      // Obtém o valor mínimo para a região do cliente
      const clientState = order.customerState;
      let minOrderValue = 600.0; // Valor mínimo padrão

      if (
        orderValidationConfig?.condicoes_venda[0]?.frete_cif?.estados &&
        orderValidationConfig.condicoes_venda[0].frete_cif.estados[clientState]
      ) {
        minOrderValue =
          orderValidationConfig.condicoes_venda[0].frete_cif.estados[
            clientState
          ];
      }

      // Verifica se o valor final está abaixo do mínimo da região
      if (order.total < minOrderValue) {
        return Swal.fire({
          icon: "error",
          title: "Pedido Abaixo do Valor Mínimo",
          html: `
            <div class="text-left text-sm leading-relaxed">
              <p class="mb-2">
                O valor final do pedido <b>${formatCurrency(order.total)}</b> está abaixo do mínimo exigido de 
                <b>${formatCurrency(minOrderValue)}</b> para o estado <b>${clientState}</b>.
              </p>
      
              <ul class="list-disc list-inside ml-2 mb-2">
                <li>Bruto: ${formatCurrency(order.subtotal)}</li>
                <li>Descontos: ${formatCurrency(order.totalDiscount)}</li>
                <li>Final: ${formatCurrency(order.total)}</li>
              </ul>
      
              <p class="mt-2">
                Ajuste os descontos ou adicione itens para atingir o valor mínimo.
              </p>
            </div>
          `,
          width: "500px",
          confirmButtonColor: "#dc2626",
          confirmButtonText: "OK",
        });
      }
      
    }

    const commercialError = validateOrder(order, orderValidationConfig, {
      payments: paymentTerms,
    });
    if (commercialError) {
      return Swal.fire({
        icon: "error",
        title: "Erro de Validação",
        html: commercialError.replace(/\n/g, "<br>"),
        width: "800px",
        confirmButtonColor: "#dc2626",
      });
    }

    const submitType = e.nativeEvent.submitter.value;
    const orderToSend = { ...order };
    orderToSend.status =
      submitType === "Orcamento" ? "Orcamento" : "Aguardando";
    orderToSend.isSupervised = order.isSupervised;

    setIsSubmitting(true);
    try {
      let url = `${process.env.REACT_APP_API_BASE_URL}/orders`;
      let method = "POST";
      if (existingOrder) {
        url = `${process.env.REACT_APP_API_BASE_URL}/orders/${order.id}`;
        method = "PUT";
      }

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(orderToSend),
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        Swal.fire({
          icon: "error",
          title: "Erro",
          text:
            data.message ||
            "Erro ao criar pedido. Entre em contato com a Fábrica",
          confirmButtonColor: "#dc2626",
        });
        return;
      }

      const isBudget = orderToSend.status === "Orcamento";
      let successMsg = "";
      if (existingOrder) {
        successMsg = isBudget
          ? "Orçamento atualizado com sucesso!"
          : "Pedido atualizado com sucesso!";
      } else {
        successMsg = isBudget
          ? "Orçamento salvo com sucesso!"
          : "Pedido lançado com sucesso!";
      }
      Swal.fire({
        icon: "success",
        title: "Sucesso",
        text: successMsg,
        timer: 3000,
        showConfirmButton: false,
        confirmButtonColor: "#dc2626",
      }).then(() => {
        onSave();
        onClose();
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao criar pedido. Entre em contato com a fábrica.",
        confirmButtonColor: "#dc2626",
      });
      console.error("Erro ao salvar pedido:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formTitle = existingOrder
    ? order.status === "Orcamento"
      ? "Editar Orçamento"
      : "Editar Pedido"
    : "Novo Pedido";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-x-auto h-full w-full z-50">
      <div className="relative top-6 mx-auto w-full max-w-6xl mb-6 px-4">
        <div
          className={`bg-white shadow-lg rounded-lg ${
            order.isSupervised ? "border-2 border-red-500" : ""
          }`}
        >
          {order.isSupervised && (
            <div className="bg-red-100 text-red-700 text-center py-1 rounded-t-lg font-medium text-sm">
              Pedido Supervisionado - Algumas validações comerciais foram
              desativadas
            </div>
          )}
          <div className="flex flex-col md:flex-row items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center space-x-6">
              <img
                src="https://alpesgas.com.br/wp-content/uploads/2023/01/logo-vermelha-horizontal.png"
                alt="Alpes Gás Logo"
                className="h-12 w-auto"
              />
              <div className="h-8 w-px bg-gray-200" />
              <div className="flex flex-col">
                <span className="text-xl text-gray-700 font-medium">
                  {formTitle}
                </span>
                <span className="text-sm text-gray-500">Pedido #{orderId}</span>
              </div>
            </div>

            <div className="flex items-center mt-4 md:mt-0">
              <div
                className={`flex items-center mr-4 px-3 py-1 rounded transition-colors duration-200 ${
                  order.isSupervised
                    ? "bg-red-50 border border-red-200"
                    : "bg-gray-100"
                }`}
              >
                <input
                  type="checkbox"
                  id="supervised"
                  checked={order.isSupervised}
                  onChange={(e) =>
                    handleFieldChange("isSupervised", e.target.checked)
                  }
                  className="h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="supervised"
                  className={`ml-2 block text-sm font-medium ${
                    order.isSupervised
                      ? "text-red-600 font-bold"
                      : "text-gray-700"
                  }`}
                >
                  SPS
                </label>
                <div className="relative ml-1 group cursor-help">
                  <Info className="h-5 w-5 text-red-500" />
                  <div className="absolute right-0 sm:left-1/2 top-full mt-2 w-64 sm:w-72 bg-gray-900 text-white text-xs rounded p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 ease-in-out z-50 shadow-lg transform sm:-translate-x-1/2">
                    <p className="leading-relaxed">
                      O Pedido será submetido a avaliação para autorização de
                      condições fora da regra comercial, podendo ser ou não
                      aceito.
                    </p>
                    <div className="absolute left-1/2 -top-2 w-4 h-4 bg-gray-900 transform rotate-45 -translate-x-1/2 hidden sm:block"></div>
                    <div className="absolute right-4 -top-2 w-4 h-4 bg-gray-900 transform rotate-45 sm:hidden"></div>
                  </div>
                </div>
              </div>

              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <form
              onSubmit={handleSubmit}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
            >
              {/* Cliente */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700">
                  Cliente
                </label>
                <div className="mt-1 flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={order.customerName}
                    readOnly
                    className="w-full sm:w-2/3 rounded-md border border-gray-300 p-2 bg-gray-50 cursor-pointer"
                    placeholder="Selecione um cliente"
                    onClick={() => {
                      if (!(existingOrder && order.status === "Orcamento")) {
                        setShowCustomerSearch(true);
                      }
                    }}
                  />
                  <input
                    type="text"
                    value={formatCpfCnpj(order.customerDocument)}
                    readOnly
                    className="w-full sm:w-1/3 rounded-md border border-gray-300 p-2 bg-gray-50"
                    placeholder="CNPJ"
                  />
                </div>

                {showCustomerSearch && (
                  <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border">
                    <div className="p-2 border-b">
                      <input
                        type="text"
                        value={customerSearchTerm}
                        onChange={(e) => setCustomerSearchTerm(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Buscar cliente..."
                        autoFocus
                      />
                    </div>
                    <div className="max-h-60 overflow-auto">
                      {filteredCustomers.map((customer) => (
                        <div
                          key={customer.CODIGO}
                          className="p-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => selectCustomer(customer)}
                        >
                          <div className="font-medium">
                            {customer.RAZAO_SOCIAL}
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatCpfCnpj(customer.CNPJ)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {customer.NOME_FANTASIA}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Condição de Pagamento + Desconto adicional */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Condição de Pagamento
                  </label>
                  <select
                    value={order.paymentCondition}
                    onChange={(e) => handleSelectPaymentTerm(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 p-2"
                  >
                    <option value="">Selecione...</option>
                    {[...paymentTerms]
                      .sort(
                        (a, b) => parseInt(a.E4_CODIGO) - parseInt(b.E4_CODIGO)
                      )
                      .map((pt) => (
                        <option key={pt.E4_CODIGO} value={pt.E4_CODIGO}>
                          {pt.E4_DESCRI.trim()}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Campo de Desconto Adicional */}
                {(order.paymentMethod === "cash" ||
                  (order.paymentCondition &&
                    ![
                      "049",
                      "050",
                      "051",
                      "052",
                      "053",
                      "054",
                      "055",
                      "056",
                    ].includes(order.paymentCondition))) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Desconto Adicional (%)
                    </label>
                    <input
                      type="number"
                      value={order.cashDiscount >= 0 ? order.cashDiscount : ""}
                      onChange={(e) => handleCashDiscountChange(e.target.value)}
                      placeholder="0,00"
                      className="mt-1 w-full rounded-md border border-gray-300 p-2"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Data de Programação
                  </label>
                  <input
                    type="date"
                    value={order.scheduleDate}
                    onChange={(e) =>
                      handleFieldChange("scheduleDate", e.target.value)
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 p-2"
                    style={{ paddingTop: "5px", paddingBottom: "5px" }}
                    required
                  />
                </div>
              </div>

              {/* Lista de Itens */}
              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => setIsProductSelectorOpen(true)}
                  className="w-full p-2 bg-red-600 text-white rounded-md hover:bg-red-700 mb-4 flex items-center justify-center"
                >
                  <PlusCircle className="h-5 w-5 mr-2" />
                  <span>Adicionar Item</span>
                </button>

                {/* Tabela para telas maiores */}
                <div className="hidden md:block border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Produto
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Código
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Quantidade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Preço Unit.
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Desconto (%)
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Subtotal
                        </th>
                        <th className="px-6 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="px-6 py-4 text-center text-gray-500"
                          >
                            Nenhum item adicionado
                          </td>
                        </tr>
                      ) : (
                        order.items.map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <img
                                  src={item.image}
                                  alt={item.product}
                                  className="h-12 w-12 rounded object-cover"
                                />
                                <span className="text-sm">{item.product}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">{item.code}</td>
                            <td className="px-6 py-4">
                              <input
                                type="number"
                                min="0"
                                value={
                                  tempQuantities[item.id] !== undefined
                                    ? tempQuantities[item.id]
                                    : item.quantity
                                }
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "quantity",
                                    e.target.value
                                  )
                                }
                                onBlur={() => handleQuantityBlur(item.id)}
                                className="w-20 border rounded p-1"
                              />
                              <p className="text-xs mt-1 text-gray-500">
                                Múltiplos de 5
                              </p>
                            </td>
                            <td className="px-6 py-4">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.price}
                                readOnly
                                className="w-24 border rounded p-1 bg-gray-100"
                              />
                            </td>
                            <td className="px-6 py-4">
                              <input
                                type="number"
                                min="0"
                                max={
                                  orderValidationConfig
                                    ? orderValidationConfig?.condicoes_venda[0]
                                        ?.desconto_item_max
                                    : 15
                                }
                                step="0.1"
                                value={item.discount}
                                onChange={(e) =>
                                  handleItemChange(
                                    item.id,
                                    "discount",
                                    e.target.value
                                  )
                                }
                                className="w-20 border rounded p-1"
                              />
                            </td>
                            <td className="px-6 py-4">
                              {formatCurrency(item.subtotal)}
                            </td>
                            <td className="px-6 py-4">
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(item.id)}
                                className="flex items-center space-x-1 text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                <span className="text-xs font-bold">
                                  Remover
                                </span>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Cards para telas mobile */}
                <div className="block md:hidden space-y-4">
                  {order.items.map((item) => (
                    <div
                      key={item.id}
                      className="border rounded-lg p-4 shadow-sm"
                    >
                      <div className="flex items-center">
                        <img
                          src={item.image}
                          alt={item.product}
                          className="h-12 w-12 rounded object-cover"
                        />
                        <div className="ml-3">
                          <p className="font-medium text-sm">{item.product}</p>
                          <p className="text-xs text-gray-500">
                            Código: {item.code}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600">
                            Quantidade
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={
                              tempQuantities[item.id] !== undefined
                                ? tempQuantities[item.id]
                                : item.quantity
                            }
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                "quantity",
                                e.target.value
                              )
                            }
                            onBlur={() => handleQuantityBlur(item.id)}
                            className="w-full border rounded p-1 text-xs"
                          />
                          <span className="text-xs text-gray-500">
                            Múltiplos de 5
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600">
                            Preço Unit.
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price}
                            readOnly
                            className="w-full border rounded p-1 text-xs bg-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600">
                            Desconto (%)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max={
                              orderValidationConfig
                                ? orderValidationConfig?.condicoes_venda[0]
                                    ?.desconto_item_max
                                : 15
                            }
                            step="0.1"
                            value={item.discount}
                            onChange={(e) =>
                              handleItemChange(
                                item.id,
                                "discount",
                                e.target.value
                              )
                            }
                            className="w-full border rounded p-1 text-xs"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600">
                            Subtotal
                          </label>
                          <span className="text-xs">
                            {formatCurrency(item.subtotal)}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-800 text-xs"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="font-bold">Remover</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totais do Pedido */}
                <div className="mt-6 text-right space-y-1">
                  <p className="text-sm text-gray-500">
                    Subtotal: {formatCurrency(order.subtotal)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Descontos: {formatCurrency(order.totalDiscount)}
                  </p>
                  <p className="text-xl font-bold text-red-600">
                    Total: {formatCurrency(order.total)}
                  </p>
                </div>

                {/* Botões de Ação */}
                <div className="mt-6 pt-4 border-t">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="text-red-500 mb-2 md:mb-0">
                      {isSubmitting && (
                        <div className="flex items-center ml-4">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-5 w-5 text-red-600"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            ></path>
                          </svg>
                          <span className="text-sm text-gray-600">
                            Enviando pedido...
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        value="finalize"
                        disabled={isSubmitting}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        {isSubmitting
                          ? order.status === "Orcamento"
                            ? "Transformando..."
                            : "Finalizando..."
                          : order.status === "Orcamento"
                          ? "Transformar em Pedido"
                          : "Finalizar Pedido"}
                      </button>
                      <button
                        type="submit"
                        value="Orcamento"
                        disabled={isSubmitting}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        {isSubmitting
                          ? order.status === "Orcamento"
                            ? "Salvando..."
                            : "Salvando como Orçamento"
                          : order.status === "Orcamento"
                          ? "Salvar Orçamento"
                          : "Salvar como Orçamento"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ProductSelector
        isOpen={isProductSelectorOpen}
        onClose={() => setIsProductSelectorOpen(false)}
        onSelect={handleAddProduct}
        products={products}
      />
    </div>
  );
};

export default OrderForm;
